import { useEffect, useMemo, useRef, useState } from 'react';
import { api, uploadUrl } from '../api/client.js';
import { Swal2 } from '../lib/swal.js';
import { MORPH_BTN } from '../lib/ui.js';

const AVATAR_COLORS = ['var(--accent)', '#E6A000', '#DC6030', '#3A6AAA', '#7B4A9E', '#2E86AB', '#D35400', '#1ABC9C'];

export default function TestimonialSection() {
  const [testimonials, setTestimonials] = useState([]);
  const [slide, setSlide] = useState(0);
  const [perView, setPerView] = useState(3);

  useEffect(() => {
    api.get('/testimonial').then(({ data }) => setTestimonials(data.testimonials || []));
    const onResize = () => setPerView(window.innerWidth >= 1024 ? 3 : window.innerWidth >= 768 ? 2 : 1);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const maxSlide = Math.max(0, testimonials.length - perView);
  const goto = (i) => setSlide(Math.min(Math.max(i, 0), maxSlide));

  const touchStartRef = useRef({ x: 0, y: 0 });
  const touchDeltaRef = useRef({ x: 0, y: 0 });
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
    touchDeltaRef.current = { x: 0, y: 0 };
  };
  const onTouchMove = (e) => {
    const t = e.touches[0];
    touchDeltaRef.current = { x: t.clientX - touchStartRef.current.x, y: t.clientY - touchStartRef.current.y };
  };
  const onTouchEnd = () => {
    const { x, y } = touchDeltaRef.current;
    if (Math.abs(x) > 45 && Math.abs(x) > Math.abs(y)) {
      goto(slide + (x < 0 ? 1 : -1));
    }
  };

  return (
    <section id="testimoni" className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold mb-4 border" style={{ background: 'color-mix(in oklab, var(--accent) 8%, transparent)', borderColor: 'color-mix(in oklab, var(--accent) 20%, transparent)', color: 'var(--accent)' }}>
            <span className="material-symbols-outlined">format_quote</span> TESTIMONI
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-neutral-900 dark:text-white mb-4">
            Apa Kata <span style={{ color: 'var(--accent)' }}>Klien</span> Kami?
          </h2>
        </div>

        {testimonials.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 dark:bg-neutral-800 mb-4">
              <span className="material-symbols-outlined text-4xl text-gray-400">chat</span>
            </div>
            <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-2">Belum Ada Testimonial</h3>
            <p className="text-neutral-600 dark:text-neutral-400">Jadilah yang pertama memberikan ulasan!</p>
          </div>
        ) : (
          <div className="relative">
            <div className="overflow-hidden -my-4" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
              <div className="flex gap-6 py-4 transition-transform duration-500" style={{ transform: `translateX(-${slide * (100 / perView)}%)` }}>
                {testimonials.map((t, i) => {
                  const initials = t.nama?.[0]?.toUpperCase() || '?';
                  const ac = AVATAR_COLORS[i % AVATAR_COLORS.length];
                  return (
                    <div key={t.id} className="flex-shrink-0" style={{ width: `calc(${100 / perView}% - ${(perView - 1) * 24 / perView}px)` }}>
                      <div className="group relative rounded-3xl p-8 h-full border transition-all duration-300 hover:shadow-xl hover:scale-[1.03] bg-white dark:bg-neutral-900" style={{ borderColor: 'color-mix(in oklab, var(--accent) 10%, transparent)' }}>
                        <div className="text-4xl mb-4 font-display" style={{ color: 'color-mix(in oklab, var(--accent) 20%, transparent)' }}>"</div>
                        <div className="flex gap-1 mb-4">
                          {Array.from({ length: 5 }).map((_, s) => (
                            <span key={s} className="material-symbols-outlined text-sm" style={{ color: '#FFC72C', fontVariationSettings: `'FILL' ${s < (t.rating || 5) ? 1 : 0}` }}>star</span>
                          ))}
                        </div>
                        <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed mb-6 italic">"{t.review}"</p>
                        <div className="flex items-center gap-3">
                          {t.avatar ? (
                            <img src={uploadUrl(t.avatar)} className="w-12 h-12 rounded-full object-cover" alt={t.nama} />
                          ) : (
                            <div className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-white text-lg" style={{ background: ac }}>{initials}</div>
                          )}
                          <div>
                            <div className="font-bold text-neutral-900 dark:text-white text-sm">{t.nama}</div>
                            <div className="text-xs text-neutral-400">{t.peran}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {testimonials.length > perView && (
              <div className="flex justify-center items-center gap-4 mt-8">
                <button onClick={() => goto(slide - 1)} className="hidden md:flex w-10 h-10 rounded-[20px] border items-center justify-center text-neutral-500 hover:text-forest-500 hover:bg-forest-500/10 hover:rounded-[14px] active:rounded-[9px] active:scale-95 active:!bg-forest-500 active:!text-white active:!border-forest-500 transition-all duration-300" style={{ borderColor: 'color-mix(in oklab, var(--accent) 30%, transparent)' }}>
                  <span className="material-symbols-outlined">chevron_left</span>
                </button>
                <div className="flex gap-2">
                  {Array.from({ length: maxSlide + 1 }).map((_, i) => (
                    <button key={i} onClick={() => goto(i)} className="rounded-full transition-all duration-300" style={{ width: i === slide ? 24 : 8, height: 8, background: i === slide ? 'var(--accent)' : 'color-mix(in oklab, var(--accent) 25%, transparent)' }} />
                  ))}
                </div>
                <button onClick={() => goto(slide + 1)} className="hidden md:flex w-10 h-10 rounded-[20px] border items-center justify-center text-neutral-500 hover:text-forest-500 hover:bg-forest-500/10 hover:rounded-[14px] active:rounded-[9px] active:scale-95 active:!bg-forest-500 active:!text-white active:!border-forest-500 transition-all duration-300" style={{ borderColor: 'color-mix(in oklab, var(--accent) 30%, transparent)' }}>
                  <span className="material-symbols-outlined">chevron_right</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <TestimonialForm />
    </section>
  );
}

function TestimonialForm() {
  const [form, setForm] = useState({ testi_nama: '', testi_peran: '', testi_email: '', testi_rating: 0, testi_review: '' });
  const [submitting, setSubmitting] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);

  const sectionRef = useRef(null);
  const cardRef = useRef(null);
  const [sideImg, setSideImg] = useState({ show: false, height: 0, top: 0 });

  useEffect(() => {
    function position() {
      const section = sectionRef.current;
      const card = cardRef.current;
      if (!section || !card) return;

      if (window.innerWidth < 1024) {
        setSideImg((s) => (s.show ? { ...s, show: false } : s));
        return;
      }

      const cardHeight = card.offsetHeight;
      const imageHeight = cardHeight * 0.9;

      requestAnimationFrame(() => {
        const sectionRect = section.getBoundingClientRect();
        const cardRect = card.getBoundingClientRect();
        const top = (cardRect.top - sectionRect.top) + (cardHeight - imageHeight) / 2;
        setSideImg({ show: true, height: imageHeight, top });
      });
    }

    position();
    window.addEventListener('resize', position);
    window.addEventListener('load', position);
    return () => {
      window.removeEventListener('resize', position);
      window.removeEventListener('load', position);
    };
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.testi_rating) {
      Swal2.toast('warning', 'Silakan pilih rating bintang terlebih dahulu.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/testimonial', form);
      Swal2.success('Testimoni Terkirim!', 'Terima kasih atas ulasan Anda. Testimoni akan tampil setelah ditinjau oleh tim kami.');
      setForm({ testi_nama: '', testi_peran: '', testi_email: '', testi_rating: 0, testi_review: '' });
    } catch (err) {
      Swal2.toast('error', err.response?.data?.message || 'Gagal mengirim testimoni.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full px-4 py-3.5 rounded-xl border text-sm outline-none bg-neutral-50 dark:bg-neutral-900/50 text-neutral-900 dark:text-white border-neutral-200 dark:border-neutral-700 transition-all duration-200 focus:ring-2 focus:ring-forest-500/20 focus:border-forest-500';

  const sideImgStyle = (side) => ({
    position: 'absolute',
    [side]: 20,
    top: sideImg.top,
    height: sideImg.height,
    width: 'auto',
    opacity: sideImg.show ? 0.9 : 0,
    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.12))',
    transition: 'all 0.3s ease',
  });

  return (
    <div ref={sectionRef} className="relative overflow-hidden mt-16 px-4 sm:px-6 lg:px-8">
      <div className="flex justify-center items-center gap-6 max-w-5xl mx-auto">
        {sideImg.show && (
          <img
            src="/img/assets_kiri.png"
            alt=""
            aria-hidden="true"
            className="hidden lg:block flex-shrink-0 pointer-events-none"
            style={sideImgStyle('left')}
          />
        )}

        <div ref={cardRef} className="w-full max-w-[560px] rounded-3xl p-8 border bg-white dark:bg-neutral-900" style={{ borderColor: 'color-mix(in oklab, var(--accent) 15%, transparent)' }}>
          <h3 className="font-display text-xl font-bold text-neutral-900 dark:text-white mb-1">Bagikan Pengalaman Anda</h3>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6">Testimoni akan tampil setelah disetujui oleh admin.</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input required placeholder="Nama lengkap" className={inputCls} value={form.testi_nama} onChange={set('testi_nama')} />
              <input placeholder="Peran / institusi" className={inputCls} value={form.testi_peran} onChange={set('testi_peran')} />
            </div>
            <input type="email" placeholder="email@contoh.com" className={inputCls} value={form.testi_email} onChange={set('testi_email')} />
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((r) => (
                <button
                  type="button"
                  key={r}
                  onClick={() => setForm((f) => ({ ...f, testi_rating: r }))}
                  onMouseEnter={() => setHoverRating(r)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="transition-transform duration-150 hover:scale-110"
                >
                  <span
                    className={`material-symbols-outlined text-2xl transition-colors duration-150 ${r > (hoverRating || form.testi_rating) ? 'text-neutral-300 dark:text-neutral-600' : ''}`}
                    style={{
                      color: r <= (hoverRating || form.testi_rating) ? '#FFC72C' : undefined,
                      fontVariationSettings: `'FILL' ${r <= (hoverRating || form.testi_rating) ? 1 : 0}`,
                    }}
                  >
                    star
                  </span>
                </button>
              ))}
            </div>
            <textarea required rows={4} placeholder="Ceritakan pengalaman Anda..." className={`${inputCls} resize-none`} value={form.testi_review} onChange={set('testi_review')} />
            <button disabled={submitting} type="submit" className={`w-full py-4 rounded-xl font-bold text-white text-sm tracking-wide shadow-md hover:shadow-xl disabled:opacity-60 ${MORPH_BTN}`} style={{ background: 'linear-gradient(135deg, var(--accent), var(--accent-hover))' }}>
              {submitting ? 'Mengirim...' : 'Kirim Testimoni'}
            </button>
          </form>
        </div>

        {sideImg.show && (
          <img
            src="/img/assets_kanan.png"
            alt=""
            aria-hidden="true"
            className="hidden lg:block flex-shrink-0 pointer-events-none"
            style={sideImgStyle('right')}
          />
        )}
      </div>
    </div>
  );
}